import { __rest } from 'tslib';
import React, { forwardRef } from 'react';
import { Radio, Sparkle, RefreshCcw } from '@piiqtechnologies/svg-icons';
import { PeekChip, PeekTooltip } from '@piiqtechnologies/ui-components';

// Every chip What's New shows (the status, Live, the project, and the drawer's New / Updated /
// Viewed) is one of Peek's own chips, as the design system draws them (Storybook "Design System/Data
// Display/Chip"): a small PeekChip outlined in a palette colour, never painted with colours of its
// own. What a chip says (`NotificationChipKind`) picks its variant (`NotificationChipVariant`), so
// each one looks the same wherever it shows.
/** The design system's chip variants What's New uses. Teal is the palette's secondary colour, Discover's. */
var NotificationChipVariant;
(function (NotificationChipVariant) {
    NotificationChipVariant["OutlinedYellow"] = "outlined-yellow";
    NotificationChipVariant["OutlinedGreen"] = "outlined-green";
    NotificationChipVariant["OutlinedBlue"] = "outlined-blue";
    NotificationChipVariant["OutlinedTeal"] = "outlined-teal";
    NotificationChipVariant["OutlinedGrey"] = "outlined-grey";
})(NotificationChipVariant || (NotificationChipVariant = {}));
const VARIANT_PROPS = {
    [NotificationChipVariant.OutlinedYellow]: { color: "warning" },
    [NotificationChipVariant.OutlinedGreen]: { color: "success" },
    // Blue isn't a MUI colour: the design system's blue chip sets it on the text, border and icon.
    [NotificationChipVariant.OutlinedBlue]: {
        sx: { color: "blue.300", borderColor: "blue.300", "& .MuiChip-icon": { color: "blue.300" } },
    },
    [NotificationChipVariant.OutlinedTeal]: { color: "secondary" },
    [NotificationChipVariant.OutlinedGrey]: { color: "info" },
};
/** What a chip says. */
var NotificationChipKind;
(function (NotificationChipKind) {
    NotificationChipKind["Draft"] = "draft";
    NotificationChipKind["Scheduled"] = "scheduled";
    NotificationChipKind["Published"] = "published";
    NotificationChipKind["Live"] = "live";
    NotificationChipKind["Discover"] = "discover";
    NotificationChipKind["New"] = "new";
    NotificationChipKind["Updated"] = "updated";
    NotificationChipKind["Viewed"] = "viewed";
})(NotificationChipKind || (NotificationChipKind = {}));
const NOTIFICATION_CHIPS = {
    [NotificationChipKind.Draft]: { label: "Draft", variant: NotificationChipVariant.OutlinedYellow },
    [NotificationChipKind.Scheduled]: { label: "Scheduled", variant: NotificationChipVariant.OutlinedBlue },
    [NotificationChipKind.Published]: { label: "Published", variant: NotificationChipVariant.OutlinedGreen },
    [NotificationChipKind.Live]: { label: "Live", variant: NotificationChipVariant.OutlinedGreen, Icon: Radio },
    [NotificationChipKind.Discover]: { label: "Discover", variant: NotificationChipVariant.OutlinedTeal },
    [NotificationChipKind.New]: { label: "New", variant: NotificationChipVariant.OutlinedTeal, Icon: Sparkle },
    [NotificationChipKind.Updated]: {
        label: "Updated",
        variant: NotificationChipVariant.OutlinedTeal,
        Icon: RefreshCcw,
    },
    [NotificationChipKind.Viewed]: { label: "Viewed", variant: NotificationChipVariant.OutlinedGrey },
};
/** One of What's New's chips. Forwards its ref, so a tooltip can anchor on it. */
const NotificationChip = forwardRef((_a, ref) => {
    var { kind, label, tooltip, sx } = _a, props = __rest(_a, ["kind", "label", "tooltip", "sx"]);
    const { label: ownLabel, variant, Icon } = NOTIFICATION_CHIPS[kind];
    const _b = VARIANT_PROPS[variant], { sx: variantSx } = _b, variantProps = __rest(_b, ["sx"]);
    const chip = (React.createElement(PeekChip, Object.assign({ ref: ref, variant: "outlined", size: "small", label: label !== null && label !== void 0 ? label : ownLabel, icon: Icon && React.createElement(Icon, { width: 12, "aria-hidden": true }), "data-testid": `notification-chip-${kind}`, tabIndex: tooltip ? 0 : undefined }, variantProps, props, { 
        // The design system's chips with an icon sit it closer to the edge.
        sx: [variantSx !== null && variantSx !== void 0 ? variantSx : {}, Icon ? { pl: 0.6 } : {}, ...(Array.isArray(sx) ? sx : [sx !== null && sx !== void 0 ? sx : {}])] })));
    return tooltip ? (React.createElement(PeekTooltip, { title: tooltip, placement: "top", describeChild: true }, chip)) : (chip);
});
NotificationChip.displayName = "NotificationChip";
const STATUS_CHIPS = {
    draft: NotificationChipKind.Draft,
    scheduled: NotificationChipKind.Scheduled,
    published: NotificationChipKind.Published,
};
const labelOfStatus = (status) => NOTIFICATION_CHIPS[STATUS_CHIPS[status]].label;
const NOTIFICATION_STATUS_LABELS = {
    draft: labelOfStatus("draft"),
    scheduled: labelOfStatus("scheduled"),
    published: labelOfStatus("published"),
};
/** An announcement's status: the admin list, its preview and the form's title. */
const NotificationStatusChip = forwardRef((_a, ref) => {
    var { status } = _a, props = __rest(_a, ["status"]);
    return React.createElement(NotificationChip, Object.assign({ ref: ref, kind: STATUS_CHIPS[status] }, props));
});
NotificationStatusChip.displayName = "NotificationStatusChip";

export { NOTIFICATION_CHIPS, NOTIFICATION_STATUS_LABELS, NotificationChip, NotificationChipKind, NotificationChipVariant, NotificationStatusChip, NotificationChip as default };
